import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Statistic, Spin, Avatar, Space, Tabs, List, Tag, Empty, Button, Progress, Table, Result } from 'antd';
import {
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StarOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  BarChartOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  ArrowDownOutlined,
  TrophyOutlined,
  UserOutlined
} from '@ant-design/icons';
import styles from './UserCenter.module.css';
import { useAuth } from '../../contexts/AuthContext';
import { message } from 'antd';
import api from '../../utils/api';
import LoginButton from '../../components/LoginButton/LoginButton';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const UserCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    wrongQuestions: 0
  });
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');
  const [studyTime, setStudyTime] = useState(0); // 新增：当前会话学习时长

  // 添加新的状态变量，用于功能卡片
  const [taskData, setTaskData] = useState({
    nextTask: "经济学基础概念",
    nextTaskCode: "econ101",
    completedTodayTasks: 0,
    todayTasks: 3,
    completionRate: 0,
    overallProgress: {
      knowledgeCards: { completed: 0, total: 30, percentage: 0 },
      examQuestions: { completed: 0, total: 200, percentage: 0 },
      wrongQuestions: { reviewed: 0, total: 20, percentage: 0 },
      totalPercentage: 0
    },
    recommendedTasks: []
  });
  const [wrongQuestionsCount, setWrongQuestionsCount] = useState(0);
  const [isLoadingWrongCount, setIsLoadingWrongCount] = useState(true);
  const [statistics, setStatistics] = useState({
    accuracy: 0,
    studyDuration: 0,
    streak: 0
  });
  const [statisticsLoading, setStatisticsLoading] = useState(true);
  const [lastStudyPage, setLastStudyPage] = useState(null);
  const [userData, setUserData] = useState({});
  const [examResults, setExamResults] = useState([]);
  const [error, setError] = useState(null);
  const [examLoading, setExamLoading] = useState(true);
  const [errorTrendData, setErrorTrendData] = useState([]);

  useEffect(() => {
    console.log('当前用户对象:', user);
    console.log('用户ID:', user?._id);
  }, [user]);

  useEffect(() => {
    const startTime = Date.now();
    localStorage.setItem('studyStartTime', startTime);

    if (user) {
      console.log('用户已登录，开始获取数据');

      // 使用合并的数据获取函数，只调用一次
      fetchAllUserData().catch(err => {
        console.error('获取用户数据出错:', err);
        setError('获取用户数据失败，请刷新页面重试');
        setLoading(false);
      });

      // 保留原有的学习时长更新逻辑，但减少更新频率
      const studyTimer = setInterval(() => {
        const currentTime = Date.now();
        const elapsedMinutes = Math.floor((currentTime - startTime) / 60000);
        console.log('已学习时长(分钟):', elapsedMinutes);
        setStudyTime(elapsedMinutes);

        // 每5分钟更新学习时长，而不是每分钟
        if (elapsedMinutes > 0 && elapsedMinutes % 5 === 0) {
          console.log('更新学习时长:', elapsedMinutes);
          api.post('/study/update-study-time', {
            duration: 5 // 一次更新5分钟
          }).then(response => {
            console.log('学习时长更新成功:', response.data);
            if (response.data.success && response.data.data) {
              // 更新学习时长
              const newTotalTime = response.data.data.totalTime;
              console.log('新的总学习时长:', newTotalTime);

              setStats(prev => ({
                ...prev,
                totalTime: newTotalTime
              }));

              setStatistics(prev => ({
                ...prev,
                studyDuration: newTotalTime
              }));

              // 将学习时长保存到localStorage
              localStorage.setItem('studyDuration', newTotalTime.toString());
            }
          }).catch(error => {
            console.error('更新学习时长失败:', error);
          });
        }
      }, 60000);

      // 移除低频率的数据刷新定时器，改为用户交互时刷新

      return () => {
        clearInterval(studyTimer);
      };
    } else {
      console.log('用户未登录，使用默认统计数据');
      setLoading(false);
    }
  }, [user]); // 只依赖user，确保只在用户变化时重新执行

  const fetchAllUserData = async () => {
    try {
      console.log('开始获取所有用户数据...');
      setLoading(true);
      setIsLoadingWrongCount(true);
      setStatisticsLoading(true);

      // 获取用户ID
      const userId = user?._id || user?.id || localStorage.getItem('userId');
      if (!userId) {
        console.error('fetchAllUserData: 未找到用户ID');
        throw new Error('未找到用户ID');
      }

      // 使用Promise.all并行获取所有需要的数据
      const timestamp = Date.now();

      // 获取统计数据 - 优先获取，确保学习时长等数据正确
      try {
        console.log('获取统计数据...');
        const statsResponse = await api.get(`/study/stats?_t=${timestamp}`);
        console.log('获取到的统计数据:', statsResponse.data);

        // 处理统计数据
        if (statsResponse.data.success) {
          const statsData = statsResponse.data.data || {};

          // 更新统计数据
          setStatistics({
            accuracy: statsData.correctRate || 0,
            studyDuration: statsData.totalTime || 0,
            streak: statsData.streak || 0
          });

          // 更新用户统计
          setStats(prev => ({
            ...prev,
            totalTime: statsData.totalTime,
            completedChapters: statsData.completedChapters
          }));

          // 将学习时长保存到localStorage，确保刷新后仍然可用
          if (statsData.totalTime !== undefined) {
            localStorage.setItem('studyDuration', statsData.totalTime.toString());
          }
        } else {
          // 如果API返回失败，尝试从localStorage获取之前保存的学习时长
          const savedDuration = localStorage.getItem('studyDuration');
          if (savedDuration) {
            console.log('使用保存的学习时长:', savedDuration);
            const duration = parseInt(savedDuration);
            setStatistics(prev => ({
              ...prev,
              studyDuration: duration
            }));
            setStats(prev => ({
              ...prev,
              totalTime: duration
            }));
          }
        }
      } catch (statsError) {
        console.error('获取统计数据失败:', statsError);

        // 如果获取失败，尝试从localStorage获取之前保存的学习时长
        const savedDuration = localStorage.getItem('studyDuration');
        if (savedDuration) {
          console.log('使用保存的学习时长:', savedDuration);
          const duration = parseInt(savedDuration);
          setStatistics(prev => ({
            ...prev,
            studyDuration: duration
          }));
          setStats(prev => ({
            ...prev,
            totalTime: duration
          }));
        }
      }

      // 尝试获取错题数量
      try {
        console.log('尝试获取错题数量');

        // 尝试方式1: 使用study/wrong-questions路径
        try {
          console.log('尝试方式1: 使用study/wrong-questions路径');
          const wrongQuestionsResponse = await api.get(`/study/wrong-questions?page=1&limit=1&_t=${timestamp}`);

          console.log('方式1返回错题数据:', wrongQuestionsResponse.data);

          if (wrongQuestionsResponse.data.success && wrongQuestionsResponse.data.data) {
            const wrongCount = wrongQuestionsResponse.data.data.total || 0;
            console.log('方式1设置错题数量为:', wrongCount);

            // 立即更新错题数量状态
            setWrongQuestionsCount(wrongCount);

            // 更新stats中的错题数量
            setStats(prev => ({
              ...prev,
              wrongQuestions: wrongCount
            }));

            // 将错题数量保存到localStorage，确保刷新后仍然可用
            localStorage.setItem('wrongQuestionsCount', wrongCount.toString());
            return; // 成功获取后直接返回
          }
        } catch (error1) {
          console.warn('方式1获取错题数量失败:', error1);
        }

        // 尝试方式2: 使用completions/wrong-questions路径
        try {
          console.log('尝试方式2: 使用completions/wrong-questions路径');
          const response = await fetch(`/api/completions/wrong-questions?userId=${userId}&_t=${timestamp}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            console.log('方式2返回错题数据:', data);

            if (data.success && data.total !== undefined) {
              const wrongCount = data.total;
              console.log('方式2设置错题数量为:', wrongCount);

              // 立即更新错题数量状态
              setWrongQuestionsCount(wrongCount);

              // 更新stats中的错题数量
              setStats(prev => ({
                ...prev,
                wrongQuestions: wrongCount
              }));

              // 将错题数量保存到localStorage，确保刷新后仍然可用
              localStorage.setItem('wrongQuestionsCount', wrongCount.toString());
              return; // 成功获取后直接返回
            }
          }
        } catch (error2) {
          console.warn('方式2获取错题数量失败:', error2);
        }

        // 尝试方式3: 使用错题统计API
        try {
          console.log('尝试方式3: 使用错题统计API');
          const response = await fetch(`/api/stats/wrong-questions?userId=${userId}&_t=${timestamp}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            console.log('方式3返回错题数据:', data);

            if (data.success && data.count !== undefined) {
              const wrongCount = data.count;
              console.log('方式3设置错题数量为:', wrongCount);

              // 立即更新错题数量状态
              setWrongQuestionsCount(wrongCount);

              // 更新stats中的错题数量
              setStats(prev => ({
                ...prev,
                wrongQuestions: wrongCount
              }));

              // 将错题数量保存到localStorage，确保刷新后仍然可用
              localStorage.setItem('wrongQuestionsCount', wrongCount.toString());
              return; // 成功获取后直接返回
            }
          }
        } catch (error3) {
          console.warn('方式3获取错题数量失败:', error3);
        }

        // 如果所有方式都失败，尝试从考试记录中计算错题数量
        if (examResults && examResults.length > 0) {
          console.log('尝试从考试记录中计算错题数量');
          let totalWrongCount = 0;

          examResults.forEach(exam => {
            if (exam.wrongQuestionsCount !== undefined) {
              totalWrongCount += exam.wrongQuestionsCount;
            } else if (exam.wrongCount !== undefined) {
              totalWrongCount += exam.wrongCount;
            } else if (exam.totalQuestionsCount && exam.accuracy !== undefined) {
              const correctCount = Math.round(exam.totalQuestionsCount * (exam.accuracy / 100));
              totalWrongCount += (exam.totalQuestionsCount - correctCount);
            }
          });

          console.log('从考试记录计算的错题数量:', totalWrongCount);
          setWrongQuestionsCount(totalWrongCount);
          setStats(prev => ({
            ...prev,
            wrongQuestions: totalWrongCount
          }));

          // 将错题数量保存到localStorage，确保刷新后仍然可用
          localStorage.setItem('wrongQuestionsCount', totalWrongCount.toString());
        }

      } catch (wrongError) {
        console.error('获取错题数量失败:', wrongError);

        // 如果获取失败，尝试从localStorage获取之前保存的错题数量
        const savedWrongCount = localStorage.getItem('wrongQuestionsCount');
        if (savedWrongCount) {
          console.log('使用保存的错题数量:', savedWrongCount);
          const count = parseInt(savedWrongCount);
          setWrongQuestionsCount(count);
          setStats(prev => ({
            ...prev,
            wrongQuestions: count
          }));
        } else {
          // 如果没有保存的错题数量，设置一个默认值
          console.log('设置默认错题数量: 10');
          setWrongQuestionsCount(10);
          setStats(prev => ({
            ...prev,
            wrongQuestions: 10
          }));
          localStorage.setItem('wrongQuestionsCount', '10');
        }
      }

    } catch (error) {
      console.error('获取用户数据失败:', error);

      // 如果获取失败，尝试从localStorage获取之前保存的数据
      const savedWrongCount = localStorage.getItem('wrongQuestionsCount');
      if (savedWrongCount) {
        console.log('使用保存的错题数量:', savedWrongCount);
        const count = parseInt(savedWrongCount);
        setWrongQuestionsCount(count);
        setStats(prev => ({
          ...prev,
          wrongQuestions: count
        }));
      }

      const savedDuration = localStorage.getItem('studyDuration');
      if (savedDuration) {
        console.log('使用保存的学习时长:', savedDuration);
        const duration = parseInt(savedDuration);
        setStatistics(prev => ({
          ...prev,
          studyDuration: duration
        }));
        setStats(prev => ({
          ...prev,
          totalTime: duration
        }));
      }
    } finally {
      setLoading(false);
      setIsLoadingWrongCount(false);
      setStatisticsLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/study/favorites');
      if (response.data.success) {
        setFavorites(response.data.favorites);
      }
    } catch (error) {
      message.error('获取收藏列表失败');
    }
  };

  const handleRemoveFavorite = async (favoriteId) => {
    try {
      await api.delete(`/study/favorites/${favoriteId}`);
      message.success('取消收藏成功');
      fetchFavorites();
    } catch (error) {
      message.error('取消收藏失败');
    }
  };

  const renderFavorites = () => {
    if (!favorites.length) {
      return <Empty description="暂无收藏内容" />;
    }

    return (
      <List
        itemLayout="vertical"
        dataSource={favorites}
        renderItem={item => (
          <List.Item
            key={item._id}
            actions={[
              <Space>
                <Text type="secondary">{new Date(item.addedAt).toLocaleDateString()}</Text>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveFavorite(item._id)}
                >
                  取消收藏
                </Button>
              </Space>
            ]}
          >
            <List.Item.Meta
              title={
                <Space>
                  <StarOutlined style={{ color: '#FFD700' }} />
                  <Text>{item.category}</Text>
                  {item.tags?.map(tag => (
                    <Tag key={tag} color="blue">{tag}</Tag>
                  ))}
                </Space>
              }
              description={item.notes}
            />
            <Paragraph ellipsis={{ rows: 2 }}>
              {item.content}
            </Paragraph>
          </List.Item>
        )}
      />
    );
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0小时0分钟';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}小时${mins}分钟`;
  };

  // 添加错题跳转函数
  const handleWrongQuestionsClick = () => {
    navigate('/wrong-questions');
  };

  // 获取继续学习按钮描述
  const getLastStudyPageDescription = () => {
    return "开始学习";
  };

  useEffect(() => {
    // 获取用户数据
    const fetchUserData = async () => {
      try {
        // 检查缓存
        const profileCacheKey = 'userProfileCache';
        const profileCacheTime = localStorage.getItem('userProfileCacheTime');
        const currentTime = Date.now();

        // 如果缓存时间不超过10分钟，使用缓存数据
        if (profileCacheTime && (currentTime - parseInt(profileCacheTime)) < 600000) {
          const cachedProfile = JSON.parse(localStorage.getItem(profileCacheKey) || '{}');
          if (cachedProfile.userInfo) {
            console.log('使用缓存的用户资料');
            setUserData(cachedProfile.userInfo);
            return;
          }
        }

        // 添加时间戳避免缓存
        const timestamp = Date.now();
        const response = await api.get(`/user/profile?_t=${timestamp}`);
        console.log('获取到的用户资料:', response.data);

        // 缓存用户资料
        localStorage.setItem('userProfileCacheTime', currentTime.toString());
        localStorage.setItem(profileCacheKey, JSON.stringify(response.data));

        setUserData(response.data.userInfo || {});
      } catch (error) {
        console.error('获取用户资料失败:', error);
        setUserData({
          nickname: user?.username || '用户',
          avatar: '',
        });
      }
    };

    fetchUserData();

    // 获取用户ID并调用API - 优化为只获取一次
    const getUserIdAndFetchData = async () => {
      try {
        // 检查缓存中是否已有userId
        const userId = user?._id || user?.id || localStorage.getItem('userId');

        if (userId) {
          console.log('使用已有用户ID:', userId);
          // 使用同一个时间戳
          const timestamp = Date.now();
          // 合并请求，一次性获取考试记录和错题
          fetchExamRecordsAndWrongQuestions(userId, timestamp);
        } else {
          console.log('尝试从/user/info获取用户信息');
          const response = await fetch('/api/user/info', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          const data = await response.json();
          console.log('从/user/info获取的用户信息:', data);

          let newUserId = null;

          if (data.success && data.user) {
            newUserId = data.user.id || data.user._id;
            console.log('从API获取到用户ID:', newUserId);

            if (newUserId) {
              localStorage.setItem('userId', newUserId);
              console.log('已将用户ID保存到localStorage:', newUserId);
              // 使用同一个时间戳
              const timestamp = Date.now();
              fetchExamRecordsAndWrongQuestions(newUserId, timestamp);
            }
          } else {
            console.error('无法获取用户ID');
            message.error('未找到用户ID，请重新登录');
          }
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
      }
    };

    getUserIdAndFetchData();
  }, [user]);

  // 修复考试成绩获取问题 - 确保当前用户没有考试成绩时不显示任何记录
  const fetchExamRecordsAndWrongQuestions = async (userId, timestamp) => {
    if (!userId) {
      console.error('fetchExamRecordsAndWrongQuestions: 未提供用户ID');
      return;
    }

    console.log('开始获取考试记录和错题，用户ID:', userId);
    setExamLoading(true);

    try {
      // 清除之前的考试记录缓存，确保获取最新数据
      clearExamCache();

      // 尝试多种方式获取考试记录
      let examData = null;
      let success = false;

      // 方式1: 使用userId参数，增加limit参数为20
      try {
        console.log('尝试方式1: 使用userId参数获取考试记录');
        const examResponse = await fetch(`/api/completions/direct?timestamp=${timestamp}&limit=20&userId=${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (examResponse.ok) {
          examData = await examResponse.json();
          console.log('方式1返回考试记录数据:', examData);

          if (examData.success && (examData.examHistory?.length > 0 || examData.records?.length > 0)) {
            success = true;
          } else {
            throw new Error('方式1未返回有效数据');
          }
        } else {
          throw new Error('方式1 API调用失败');
        }
      } catch (error1) {
        console.warn('方式1调用失败:', error1.message);

        // 方式2: 不使用userId参数，依赖后端从token获取用户ID，增加limit参数为20
        try {
          console.log('尝试方式2: 不使用userId参数获取考试记录');
          const examResponse = await fetch(`/api/completions/direct?timestamp=${timestamp}&limit=20`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (examResponse.ok) {
            examData = await examResponse.json();
            console.log('方式2返回考试记录数据:', examData);

            if (examData.success && (examData.examHistory?.length > 0 || examData.records?.length > 0)) {
              success = true;
            } else {
              throw new Error('方式2未返回有效数据');
            }
          } else {
            throw new Error('方式2 API调用失败');
          }
        } catch (error2) {
          console.warn('方式2调用失败:', error2.message);

          // 方式3: 使用另一个API路径，不设置limit参数
          try {
            console.log('尝试方式3: 使用另一个API路径');
            const examResponse = await fetch(`/api/exam/history?userId=${userId}&_t=${timestamp}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            if (examResponse.ok) {
              examData = await examResponse.json();
              console.log('方式3返回考试记录数据:', examData);

              if (examData.success && examData.data && examData.data.length > 0) {
                // 调整数据格式以匹配之前的格式
                examData = {
                  success: true,
                  examHistory: examData.data
                };
                success = true;
              } else {
                throw new Error('方式3未返回有效数据');
              }
            } else {
              throw new Error('方式3 API调用失败');
            }
          } catch (error3) {
            console.warn('方式3调用失败:', error3.message);

            // 所有API调用都失败，设置为空数组
            console.log('所有API调用都失败，设置为空数组');
            setExamResults([]);

            // 清除localStorage中的考试记录缓存
            localStorage.removeItem('examResults');
          }
        }
      }

      // 处理考试记录数据
      if (success && examData) {
        let examRecords = [];

        if (examData.examHistory && examData.examHistory.length > 0) {
          console.log('使用examHistory数据:', examData.examHistory);
          examRecords = examData.examHistory;
        } else if (examData.records && examData.records.length > 0) {
          console.log('使用records数据:', examData.records);
          examRecords = examData.records;
        }

        if (examRecords.length > 0) {
          console.log('获取到的考试记录数量:', examRecords.length);

          // 确保按日期排序，最新的在前面
          examRecords.sort((a, b) => {
            const dateA = new Date(a.examDate || a.completionDate || 0);
            const dateB = new Date(b.examDate || b.completionDate || 0);
            return dateB - dateA; // 降序排列，最新的在前面
          });

          // 更新状态
          setExamResults(examRecords);

          // 缓存获取到的数据
          localStorage.setItem('examResults', JSON.stringify(examRecords));
        } else {
          console.warn('没有考试记录数据');
          setExamResults([]);

          // 清除localStorage中的考试记录缓存
          localStorage.removeItem('examResults');
        }
      } else {
        console.warn('获取考试记录失败');
        setExamResults([]);

        // 清除localStorage中的考试记录缓存
        localStorage.removeItem('examResults');
      }
    } catch (error) {
      console.error('获取考试记录和错题错误:', error);
      setExamResults([]);

      // 清除localStorage中的考试记录缓存
      localStorage.removeItem('examResults');
    } finally {
      setExamLoading(false);
    }
  };

  // 修改清除缓存函数，确保彻底清除所有考试记录相关的缓存
  const clearExamCache = () => {
    const userId = user?._id || user?.id || localStorage.getItem('userId');
    if (userId) {
      const examCacheKey = `examRecordsCache_${userId}`;
      localStorage.removeItem(examCacheKey);
      localStorage.removeItem(`examCacheTime_${userId}`);
    }

    // 清除通用缓存
    localStorage.removeItem('examResults');
    localStorage.removeItem('errorTrendCache');
    localStorage.removeItem('trendCacheTime');

    console.log('已清除所有考试记录缓存');
  };

  // 组件初始化时，不从localStorage加载考试记录，而是强制获取最新数据
  useEffect(() => {
    // 清除之前的考试记录缓存
    clearExamCache();

    // 获取用户ID并调用API获取最新数据
    const userId = user?._id || user?.id || localStorage.getItem('userId');
    if (userId) {
      const timestamp = Date.now();
      fetchExamRecordsAndWrongQuestions(userId, timestamp);
    }
  }, [user]);

  // 修复：添加回examColumns定义
  const examColumns = [
    {
      title: '考试名称',
      dataIndex: 'examName',
      key: 'examName',
      render: (text, record) => {
        // 使用testSetName作为显示文本，如果不存在则使用examName
        const displayName = record.testSetName || text || '未命名考试';

        // 添加点击处理函数
        const handleExamClick = () => {
          // 记录点击事件
          console.log('点击考试:', record);

          // 直接使用examType字段，如果存在的话
          let collectionName = record.examType;

          // 如果没有examType字段，尝试从其他字段提取
          if (!collectionName) {
            // 1. 从examId中提取信息
            if (record.examId && record.examId.includes('_')) {
              const parts = record.examId.split('_');
              if (parts.length >= 2) {
                collectionName = parts[0];
              }
            }

            // 2. 从testSetName中提取信息
            if (!collectionName && displayName) {
              // 根据考试名称判断类型
              if (displayName.includes('教师') || displayName.includes('冲刺') || displayName.includes('上-')) {
                collectionName = 'teacher_exam';
              } else if (displayName.includes('建设') || displayName.includes('工程') || displayName.includes('法规')) {
                collectionName = 'construction_exam';
              } else if (displayName.includes('机电')) {
                collectionName = 'electrical_exam';
              } else if (displayName.includes('经济') || displayName.includes('会计')) {
                collectionName = 'accounting_exam';
              }
            }

            // 3. 如果仍然无法确定，使用默认值
            if (!collectionName) {
              collectionName = 'teacher_exam';
            }
          }

          // 构建URL并导航
          const url = `/exam/practice/${collectionName}/${encodeURIComponent(displayName)}?examCategory=${record.examCategory || ''}`;
          console.log('跳转到:', url, '集合名称:', collectionName);
          navigate(url);
        };

        return (
          <a
            className="exam-name"
            onClick={handleExamClick}
            style={{ color: '#13c2c2', cursor: 'pointer' }}
          >
            {displayName}
          </a>
        );
      }
    },
    {
      title: '考试日期',
      dataIndex: 'examDate',
      key: 'examDate',
      render: (date) => {
        // 确保日期是有效的
        if (!date) return '未知日期';

        // 尝试将字符串转换为日期对象
        const dateObj = new Date(date);

        // 检查日期是否有效
        if (isNaN(dateObj.getTime())) return '无效日期';

        // 格式化日期为 YYYY-MM-DD HH:MM
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
      }
    },
    {
      title: '得分',
      dataIndex: 'score',
      key: 'score',
      render: (score) => <span className="exam-score">{score !== undefined ? score.toFixed(2) : '未评分'}</span>
    },
    {
      title: '错题数量',
      dataIndex: 'wrongQuestionsCount',
      key: 'wrongQuestionsCount',
      render: (wrongQuestionsCount, record) => {
        // 首先检查是否有wrongQuestionsCount字段
        if (wrongQuestionsCount !== undefined) {
          return wrongQuestionsCount;
        }

        // 然后检查是否有wrongCount字段
        if (record.wrongCount !== undefined) {
          return record.wrongCount;
        }

        // 最后尝试根据总题数和正确题数计算错题数量
        if (record.totalQuestionsCount && record.accuracy !== undefined) {
          // 根据准确率计算错题数量
          const correctCount = Math.round(record.totalQuestionsCount * (record.accuracy / 100));
          return record.totalQuestionsCount - correctCount;
        } else if (record.totalQuestions && record.correctCount !== undefined) {
          // 使用旧的字段名
          return record.totalQuestions - record.correctCount;
        }

        return '未记录';
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusText = status === 'passed' ? '通过' : '未通过';
        const statusClass = status === 'passed' ? 'status-passed' : 'status-failed';
        return <span className={`exam-status ${statusClass}`}>{statusText}</span>;
      }
    },
    {
      title: '正确率',
      key: 'accuracy',
      render: (_, record) => {
        // 检查是否有正确题目数和总题目数
        if (record.correctCount !== undefined && record.totalQuestions) {
          const accuracy = (record.correctCount / record.totalQuestions * 100).toFixed(1);
          return `${accuracy}% (${record.correctCount}/${record.totalQuestions})`;
        }
        return '未知';
      }
    }
  ];

  // 获取错题趋势数据
  useEffect(() => {
    const fetchErrorTrendData = async () => {
      try {
        console.log('开始获取错题趋势数据...');

        // 检查是否有考试结果数据
        if (!examResults || examResults.length === 0) {
          console.log('没有考试结果数据，不生成趋势图');
          setErrorTrendData([]);
          return;
        }

        // 强制刷新趋势图数据，不使用缓存
        const forceRefresh = true;

        // 检查缓存
        const trendCacheKey = 'errorTrendCache';
        const trendCacheTime = localStorage.getItem('trendCacheTime');
        const currentTime = Date.now();

        // 如果不强制刷新且缓存时间不超过10分钟，使用缓存数据
        if (!forceRefresh && trendCacheTime && (currentTime - parseInt(trendCacheTime)) < 600000) {
          const cachedTrend = JSON.parse(localStorage.getItem(trendCacheKey) || '[]');
          if (cachedTrend.length > 0) {
            console.log('使用缓存的错题趋势数据');
            setErrorTrendData(cachedTrend);
            return;
          }
        }

        // 使用考试结果数据生成趋势图
        console.log('使用考试结果数据生成趋势图，考试记录数量:', examResults.length);

        // 从考试历史记录中提取错题数据
        let examHistory = [...examResults];

        // 确保数据按时间排序，最新的在最后
        examHistory.sort((a, b) => {
          const dateA = new Date(a.examDate || a.completionDate || 0);
          const dateB = new Date(b.examDate || b.completionDate || 0);
          return dateA - dateB;
        });

        // 只取最近7次考试
        const recentExams = examHistory.slice(-7);
        console.log('最近7次考试记录:', recentExams.length, '条');

        const trendData = recentExams.map((exam, index) => {
          // 计算错题数量和总题数
          let wrongCount = 0;
          let totalQuestions = 0;

          // 获取总题数
          if (exam.totalQuestionsCount !== undefined) {
            totalQuestions = exam.totalQuestionsCount;
          } else if (exam.totalQuestions !== undefined) {
            totalQuestions = exam.totalQuestions;
          } else if (exam.questionCount !== undefined) {
            totalQuestions = exam.questionCount;
          } else {
            totalQuestions = 50;
          }

          // 尝试不同的字段名称获取错题数量
          if (exam.wrongQuestionsCount !== undefined) {
            wrongCount = exam.wrongQuestionsCount;
          } else if (exam.wrongCount !== undefined) {
            wrongCount = exam.wrongCount;
          } else {
            // 计算正确题数
            let correctCount = 0;
            if (exam.correctCount !== undefined) {
              correctCount = exam.correctCount;
            } else if (exam.accuracy !== undefined && totalQuestions > 0) {
              correctCount = Math.round(totalQuestions * (exam.accuracy / 100));
            } else if (exam.score !== undefined && totalQuestions > 0) {
              correctCount = Math.round(totalQuestions * (exam.score / 100));
            }

            // 计算错题数量
            if (totalQuestions > 0) {
              wrongCount = totalQuestions - correctCount;
            } else {
              wrongCount = 10;
            }
          }

          // 提取考试名称作为标签
          let label = '考试';
          if (exam.examName) {
            label = exam.examName.length > 4 ? exam.examName.substring(0, 4) : exam.examName;
          } else if (exam.testSetName) {
            label = exam.testSetName.length > 4 ? exam.testSetName.substring(0, 4) : exam.testSetName;
          } else if (exam.examDate) {
            const date = new Date(exam.examDate);
            if (!isNaN(date.getTime())) {
              label = `${date.getMonth() + 1}/${date.getDate()}`;
            }
          } else {
            label = `${(index + 1).toString().padStart(2, '0')}`;
          }

          // 添加日期信息，用于排序
          const examDate = new Date(exam.examDate || exam.completionDate || 0);

          return {
            date: label,
            totalCount: totalQuestions,
            wrongCount: wrongCount,
            examName: exam.examName || exam.testSetName || '未命名考试',
            timestamp: examDate.getTime()
          };
        });

        console.log('从考试历史记录生成的趋势数据:', trendData);

        // 缓存趋势数据
        localStorage.setItem('trendCacheTime', currentTime.toString());
        localStorage.setItem(trendCacheKey, JSON.stringify(trendData));

        setErrorTrendData(trendData);
      } catch (error) {
        console.error('获取错题趋势数据失败:', error);
        setErrorTrendData([]);
      }
    };

    // 当examResults变化时立即获取趋势数据
    if (examResults && examResults.length > 0) {
      fetchErrorTrendData();
    }
  }, [examResults]);

  // 修改错题趋势图组件，确保当没有考试记录时显示"暂无数据"
  const ErrorTrendChart = () => {
    const [hoveredBar, setHoveredBar] = useState(null);
    const navigate = useNavigate();

    // 添加跳转到错题页面的函数
    const goToWrongQuestions = () => {
      navigate('/wrong-questions');
    };

    // 检查是否有真实的考试数据
    const hasRealExamData = examResults && examResults.length > 0;

    // 如果没有真实的考试数据，显示"暂无数据"
    if (!hasRealExamData) {
      return (
        <div className={styles.trendCardContainer}>
          <div className={styles.trendHeader}>
            <span className={styles.trendTitle}>错题趋势</span>
          </div>

          <div className={styles.emptyTrendContainer} style={{
            height: '180px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#8c8c8c',
            fontSize: '14px',
            flexDirection: 'column'
          }}>
            <div style={{ marginBottom: '10px' }}>暂无考试数据</div>
            <Button
              type="primary"
              size="small"
              onClick={goToWrongQuestions}
              style={{
                backgroundColor: '#13c2c2',
                borderColor: '#13c2c2',
                fontSize: '12px',
                height: '28px',
                padding: '0 10px'
              }}
            >
              去做题
            </Button>
          </div>
        </div>
      );
    }

    if (!errorTrendData || errorTrendData.length === 0) {
      return (
        <div className={styles.trendCardContainer}>
          <div className={styles.trendHeader}>
            <span className={styles.trendTitle}>错题趋势</span>
          </div>
          <div style={{ padding: '10px', textAlign: 'center', color: '#8c8c8c', height: '180px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            加载中...
          </div>
        </div>
      );
    }

    // 使用所有趋势数据，不再进行切片
    let displayData = [...errorTrendData];

    // 确保数据是按时间顺序排列的，最新的在最后（最右侧）
    displayData.sort((a, b) => a.timestamp - b.timestamp);

    console.log('错题趋势图显示数据:', displayData.length, '条记录');

    const maxTotalCount = Math.max(...displayData.map(item => item.totalCount || 0));
    const maxWrongCount = Math.max(...displayData.map(item => item.wrongCount || 0));
    const maxCount = Math.max(maxTotalCount, maxWrongCount, 50); // 确保至少有一个最小高度

    // 计算趋势变化 - 比较最后两次考试的错题数量
    let isDecreasing = false;
    let trendPercentage = 0;

    if (displayData.length > 1) {
      const lastIndex = displayData.length - 1;
      const lastCount = displayData[lastIndex].wrongCount;
      const prevCount = displayData[lastIndex - 1].wrongCount;

      isDecreasing = lastCount < prevCount;

      if (prevCount > 0) {
        // 计算变化百分比
        trendPercentage = Math.round(((lastCount - prevCount) / prevCount) * 100);
      }
    }

    return (
      <div className={styles.trendCardContainer}>
        <div className={styles.trendHeader}>
          <span className={styles.trendTitle}>错题趋势</span>
          <span className={isDecreasing ? styles.trendBadgeDecreasing : styles.trendBadgeIncreasing}>
            {isDecreasing ?
              <span>↓ 减少中 {Math.abs(trendPercentage)}%</span> :
              <span>↑ 增加中 {Math.abs(trendPercentage)}%</span>
            }
          </span>
        </div>

        <div className={styles.trendChartContainer}>
          {/* 添加网格背景 - 只保留水平虚线 */}
          <div className={styles.gridLines}>
            {/* 水平虚线网格线 */}
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={`h-grid-${i}`}
                className={styles.horizontalGridLine}
                style={{ top: `${i * 25}%` }}
              />
            ))}
          </div>

          {/* 柱状图区域 - 添加点击事件 */}
          <div
            className={styles.barsContainer}
            onClick={goToWrongQuestions}
            style={{ cursor: 'pointer' }}
          >
            {displayData.map((item, index) => {
              // 计算柱状图高度，确保即使是小值也有最小高度
              const totalBarHeight = maxCount > 0
                ? Math.max(5, (item.totalCount / maxCount) * 80) // 调整最大高度为80px
                : 5;

              const wrongBarHeight = maxCount > 0
                ? Math.max(5, (item.wrongCount / maxCount) * 80) // 调整最大高度为80px
                : 5;

              return (
                <div
                  key={index}
                  className={styles.barGroup}
                  onMouseEnter={() => setHoveredBar(index)}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  <div className={styles.barPair}>
                    {/* 总题数柱状图 */}
                    <div
                      className={styles.totalBar}
                      style={{ height: `${totalBarHeight}px` }}
                    ></div>

                    {/* 错题数柱状图 */}
                    <div
                      className={styles.wrongBar}
                      style={{ height: `${wrongBarHeight}px` }}
                    >
                      {/* 显示错题数量和考试名称的提示 */}
                      {hoveredBar === index && (
                        <div className={styles.barTooltip}>
                          <div className={styles.tooltipExamName}>{item.examName || '未命名考试'}</div>
                          <div className={styles.tooltipCounts}>
                            <span className={styles.tooltipTotalCount}>总题数: {item.totalCount}</span>
                            <span className={styles.tooltipWrongCount}>错题: {item.wrongCount}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 底部标签 - 使用数字 */}
          <div className={styles.labelsContainer}>
            {displayData.map((_, index) => (
              <div
                key={index}
                className={styles.labelItem}
              >
                <div className={styles.labelText}>
                  {(index + 1).toString().padStart(2, '0')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 图例和复习错题按钮 */}
        <div className={styles.legendContainer} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* 左侧图例 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className={styles.legendItem} style={{ marginRight: '15px' }}>
              <div className={styles.legendColorTotal}></div>
              <span>总题数</span>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendColorWrong}></div>
              <span>错题数</span>
            </div>
          </div>

          {/* 右侧复习错题按钮 */}
          <Button
            type="primary"
            size="small"
            onClick={goToWrongQuestions}
            style={{
              backgroundColor: '#13c2c2',
              borderColor: '#13c2c2',
              fontSize: '12px',
              height: '28px',
              padding: '0 10px'
            }}
          >
            复习错题
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  // 添加错误处理显示
  if (error) {
    return (
      <div className={styles.container}>
        <Card>
          <Result
            status="warning"
            title="加载失败"
            subTitle={error}
            extra={
              <Button type="primary" onClick={() => window.location.reload()}>
                刷新页面
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 三张大卡片布局 */}
      <Row gutter={[24, 24]}>
        {/* 用户基本信息卡片 */}
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <div className={styles.userCardContainer}>
            <Avatar size={80} src={user?.profile?.avatar}>
              {user?.profile?.nickname?.[0] || user?.username?.[0] || 'U'}
            </Avatar>
            <Title level={3} className={styles.userTitle}>
              {user?.profile?.nickname || user?.username || 'michael'}
            </Title>
            <Text type="secondary" className={styles.userLoginTime}>
              上次登录: {user?.stats?.lastLoginTime ? new Date(user?.stats?.lastLoginTime).toLocaleString() : '2025/3/14 21:02:40'}
            </Text>
          </div>
        </Col>

        {/* 统计与报告卡片 */}
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <div className={styles.statsCardContainer}>
            <div className={styles.statsGrid}>
              <div className={styles.statItem}>
                <div className={styles.statValueBlue}>
                  {taskData.todayTasks}
                </div>
                <div className={styles.statLabel}>
                  今日任务
                </div>
              </div>

              <div className={styles.statItem}>
                <div className={styles.statValueGreen}>
                  {isLoadingWrongCount ? <Spin size="small" /> : (
                    // 优先使用wrongQuestionsCount，如果为0也显示0，不再使用默认值
                    wrongQuestionsCount !== undefined ? wrongQuestionsCount : (
                      // 如果wrongQuestionsCount未定义，尝试使用stats.wrongQuestions
                      stats.wrongQuestions !== undefined ? stats.wrongQuestions : (
                        // 最后尝试从localStorage获取
                        localStorage.getItem('wrongQuestionsCount') ? parseInt(localStorage.getItem('wrongQuestionsCount')) : 0
                      )
                    )
                  )}
                </div>
                <div className={styles.statLabel}>
                  错题数量
                </div>
              </div>

              <div className={styles.statItem}>
                <div className={styles.statValueYellow}>
                  {statistics.accuracy}%
                </div>
                <div className={styles.statLabel}>
                  平均正确率
                </div>
              </div>

              <div className={styles.statItem}>
                <div className={styles.statValueCyan}>
                  <ClockCircleOutlined style={{ fontSize: '16px', marginRight: '4px' }} />
                  {statisticsLoading ? <Spin size="small" /> : (
                    // 优先使用statistics.studyDuration
                    statistics.studyDuration > 0 ?
                      (statistics.studyDuration / 60).toFixed(1) + 'h' : (
                      // 如果statistics.studyDuration为0，尝试使用stats.totalTime
                      stats.totalTime > 0 ?
                        (stats.totalTime / 60).toFixed(1) + 'h' : (
                        // 最后尝试从localStorage获取
                        localStorage.getItem('studyDuration') ?
                          (parseInt(localStorage.getItem('studyDuration')) / 60).toFixed(1) + 'h' :
                          '0.0h'
                      )
                    )
                  )}
                </div>
                <div className={styles.statLabel}>
                  学习时长
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* 错题趋势卡片 */}
        <Col xs={24} sm={24} md={8} lg={8} xl={8}>
          <ErrorTrendChart />
        </Col>
      </Row>

      {/* 最近考试成绩列表 */}
      <Card
        className={styles.examResultsCard}
        style={{ marginTop: 24 }}
        bordered={false}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CalendarOutlined style={{ marginRight: 8, color: '#13c2c2', fontSize: 18 }} />
            <Title level={4} style={{ margin: 0 }}>最近考试成绩</Title>
          </div>
          <div>

            {examResults && examResults.length > 0 && (
              <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                共 {examResults.length} 条记录
              </span>
            )}
          </div>
        </div>
        {examLoading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (!examResults || examResults.length === 0) ? (
          <div style={{
            padding: '40px 0',
            textAlign: 'center',
            color: '#13c2c2',
            fontSize: '16px'
          }}>
            暂时没有考试成绩
            <div style={{ marginTop: '10px' }}>
              <Button
                type="primary"
                onClick={() => {
                  // 清除缓存并强制刷新考试记录
                  clearExamCache();
                  const userId = user?._id || user?.id || localStorage.getItem('userId');
                  if (userId) {
                    const timestamp = Date.now();
                    fetchExamRecordsAndWrongQuestions(userId, timestamp);
                  }
                }}
                style={{
                  backgroundColor: '#13c2c2',
                  borderColor: '#13c2c2'
                }}
              >
                刷新考试记录
              </Button>
            </div>
          </div>
        ) : (
          <Table
            columns={examColumns}
            dataSource={examResults}
            rowKey={(record) => record.id || record._id || record.examId || `${record.examDate}_${record.examName}`}
            pagination={{
              pageSize: 5, // 每页显示5条记录
              showSizeChanger: true, // 允许用户更改每页显示的记录数
              pageSizeOptions: ['5', '10', '20', '50'], // 可选的每页记录数
              showTotal: (total) => `共 ${total} 条记录` // 显示总记录数
            }}
            locale={{ emptyText: '暂无考试记录' }}
          />
        )}
      </Card>
    </div>
  );
};

export default UserCenter;